import React, { useState, useEffect } from 'react';
import { AlertTriangle, ArrowUpCircle, Link2Off, DollarSign, Building2, X, ExternalLink } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

/**
 * Ad Spend Blocker Component
 * Shows a full-screen blocker when user exceeds their tier's ad spend limit
 * User must either upgrade or unlink accounts to continue
 */
export default function AdSpendBlocker({ onUpgrade, onManageAccounts }) {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [spendStatus, setSpendStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSpendStatus();
  }, []);

  const fetchSpendStatus = async () => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/user/ad-spend-status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSpendStatus(data);
      } else {
        setError(data.error || 'Failed to fetch status');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      navigate('/subscription');
    }
  };

  const handleManageAccounts = () => {
    if (onManageAccounts) {
      onManageAccounts();
    } else {
      navigate('/settings');
    }
  };

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000
      }}>
        <div style={{ color: 'white', fontSize: 18 }}>Checking account status...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000
      }}>
        <div style={{ color: '#ef4444', fontSize: 18 }}>Error: {error}</div>
      </div>
    );
  }

  // If not blocked, don't render anything
  if (!spendStatus?.isBlocked) {
    return null;
  }

  const overSpendAmount = spendStatus.currentSpend - spendStatus.tierLimit;
  const requiredTier = spendStatus.requiredTier;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: 20
    }}>
      <div style={{
        maxWidth: 600,
        width: '100%',
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(20px)',
        borderRadius: 24,
        border: '1px solid rgba(239, 68, 68, 0.3)',
        overflow: 'hidden',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
      }}>
        {/* Header - Red Warning */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(220, 38, 38, 0.3))',
          padding: '32px 32px 24px',
          borderBottom: '1px solid rgba(239, 68, 68, 0.2)',
          textAlign: 'center'
        }}>
          <div style={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'rgba(239, 68, 68, 0.2)',
            border: '2px solid rgba(239, 68, 68, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px'
          }}>
            <AlertTriangle size={40} style={{ color: '#ef4444' }} />
          </div>
          
          <h1 style={{
            fontSize: 28,
            fontWeight: 700,
            color: '#fca5a5',
            margin: 0,
            marginBottom: 8
          }}>
            Ad Spend Limit Exceeded
          </h1>
          
          <p style={{
            fontSize: 16,
            color: 'rgba(255, 255, 255, 0.7)',
            margin: 0
          }}>
            Your access is temporarily restricted
          </p>
        </div>

        {/* Content */}
        <div style={{ padding: 32 }}>
          {/* Current vs Limit */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 16,
            marginBottom: 24
          }}>
            <div style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              borderRadius: 12,
              padding: 20,
              textAlign: 'center'
            }}>
              <div style={{ 
                fontSize: 12, 
                color: 'rgba(255, 255, 255, 0.5)', 
                marginBottom: 4,
                textTransform: 'uppercase',
                letterSpacing: 0.5
              }}>
                Your Ad Spend (30 days)
              </div>
              <div style={{ 
                fontSize: 28, 
                fontWeight: 700, 
                color: '#ef4444'
              }}>
                {formatCurrency(spendStatus.currentSpend)}
              </div>
            </div>
            
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 12,
              padding: 20,
              textAlign: 'center'
            }}>
              <div style={{ 
                fontSize: 12, 
                color: 'rgba(255, 255, 255, 0.5)', 
                marginBottom: 4,
                textTransform: 'uppercase',
                letterSpacing: 0.5
              }}>
                {spendStatus.currentTierName} Limit
              </div>
              <div style={{ 
                fontSize: 28, 
                fontWeight: 700, 
                color: 'white'
              }}>
                {formatCurrency(spendStatus.tierLimit)}
              </div>
            </div>
          </div>

          {/* Over by amount */}
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            borderRadius: 12,
            padding: 16,
            marginBottom: 24,
            display: 'flex',
            alignItems: 'center',
            gap: 12
          }}>
            <DollarSign size={24} style={{ color: '#ef4444' }} />
            <div>
              <div style={{ fontSize: 14, color: 'rgba(255, 255, 255, 0.7)' }}>
                You're over your limit by
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#fca5a5' }}>
                {formatCurrency(overSpendAmount)}
              </div>
            </div>
          </div>

          {/* Account info */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: 12,
            padding: 16,
            marginBottom: 24,
            display: 'flex',
            alignItems: 'center',
            gap: 12
          }}>
            <Building2 size={24} style={{ color: '#8b5cf6' }} />
            <div>
              <div style={{ fontSize: 14, color: 'rgba(255, 255, 255, 0.7)' }}>
                Linked Accounts
              </div>
              <div style={{ fontSize: 16, fontWeight: 600, color: 'white' }}>
                {spendStatus.accountCount} accounts • Max {spendStatus.maxAccounts || '∞'} on {spendStatus.currentTierName}
              </div>
            </div>
          </div>

          {/* Options */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.03)',
            borderRadius: 12,
            padding: 20,
            marginBottom: 24
          }}>
            <h3 style={{ 
              fontSize: 14, 
              fontWeight: 600, 
              color: 'rgba(255, 255, 255, 0.5)',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              marginBottom: 16,
              margin: 0
            }}>
              To continue, you can:
            </h3>
            
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 12,
              marginTop: 16
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <ArrowUpCircle size={20} style={{ color: '#10b981' }} />
                <span style={{ color: 'white', fontSize: 15 }}>
                  <strong>Upgrade</strong> to {requiredTier?.name || 'a higher plan'} ({formatCurrency(requiredTier?.price || 0)}/mo)
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Link2Off size={20} style={{ color: '#f59e0b' }} />
                <span style={{ color: 'white', fontSize: 15 }}>
                  <strong>Unlink accounts</strong> to reduce your total ad spend below {formatCurrency(spendStatus.tierLimit)}
                </span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={handleUpgrade}
              style={{
                flex: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '16px 24px',
                background: 'linear-gradient(135deg, #10b981, #059669)',
                border: 'none',
                borderRadius: 12,
                color: 'white',
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              <ArrowUpCircle size={20} />
              Upgrade to {requiredTier?.name || 'Higher Plan'}
            </button>
            
            <button
              onClick={handleManageAccounts}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '16px 24px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: 12,
                color: 'white',
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
            >
              Manage Accounts
            </button>
          </div>

          {/* Contact Support */}
          <div style={{
            textAlign: 'center',
            marginTop: 24,
            paddingTop: 24,
            borderTop: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <p style={{ 
              fontSize: 13, 
              color: 'rgba(255, 255, 255, 0.5)',
              margin: 0
            }}>
              Need help? Contact{' '}
              <a 
                href="mailto:support@bidoryx.com"
                style={{ color: '#8b5cf6', textDecoration: 'none' }}
              >
                support@bidoryx.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Hook to check if user should see the blocker
 * @returns {object} - { isBlocked, isLoading, spendStatus }
 */
export function useAdSpendBlocker() {
  const { getToken } = useAuth();
  const [isBlocked, setIsBlocked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [spendStatus, setSpendStatus] = useState(null);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const token = await getToken();
        if (!token) {
          console.log('🔍 [useAdSpendBlocker] No token - skipping check');
          setIsLoading(false);
          return;
        }
        
        console.log('🔍 [useAdSpendBlocker] Checking ad spend status...');
        
        const response = await fetch(`${API_URL}/api/user/ad-spend-status`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          console.error('🔍 [useAdSpendBlocker] API error:', response.status);
          setIsLoading(false);
          return;
        }
        
        const data = await response.json();
        console.log('🔍 [useAdSpendBlocker] Status:', data);
        
        if (data.success) {
          setIsBlocked(data.isBlocked || false);
          setSpendStatus(data);
          console.log('🔍 [useAdSpendBlocker] isBlocked:', data.isBlocked);
        } else {
          console.error('🔍 [useAdSpendBlocker] API returned success=false:', data.error);
        }
      } catch (err) {
        console.error('🔍 [useAdSpendBlocker] Error:', err);
        // Don't block on error - fail open
        setIsBlocked(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkStatus();
  }, [getToken]);

  return { isBlocked, isLoading, spendStatus };
}

